export const getUserEmail = (user) => user?.email || user?.Email || '';
export const getUserFirstName = (user) => user?.firstName || user?.FirstName || '';
export const getUserLastName = (user) => user?.lastName || user?.LastName || '';
export const getUserDisplayName = (user) => {
	const fullName = `${getUserFirstName(user)} ${getUserLastName(user)}`.trim();
	return fullName || getUserEmail(user);
};
export const getProjectId = (project) => project?.projectId || project?.ProjectId || project?.id;
export const getProjectCode = (project) => project?.projectCode || project?.ProjectCode || project?.code;
export const getProjectTitle = (project) => project?.projectTitle || project?.ProjectTitle || project?.description;
export const getRoleName = (role) => role?.name || role?.Name;

export const getPrincipalKeySignature = (items, getPrincipalId) => {
	if (!Array.isArray(items) || typeof getPrincipalId !== 'function') {
		return '';
	}
	return items.map((item) => getPrincipalId(item)).join('\u001f');
};

export const getSetSignature = (set) => Array.from(set || []).sort().join('\u001f');

export const shouldResetHeightCacheForLayout = (previous, next) =>
	previous?.activeTabKey !== next?.activeTabKey ||
	previous?.itemKeySignature !== next?.itemKeySignature;

export const createUsersTabConfig = ({
	handleAddProject,
	handleRemoveProject,
	handleAddRole,
	handleRemoveRole,
	fetchUserProjects,
	fetchUserRoles,
	useAllQuery,
	useSearchQuery,
}) => ({
	key: 'users',
	label: 'Users',
	search: {
		mode: 'server',
		placeholder: 'Name or email',
		debounceMs: 400,
		minChars: 1,
	},
	emptyStateLabel: 'users',
	getData: () => [],
	filterPrincipal: (principal, searchLower) =>
		(principal.name || '').toLowerCase().includes(searchLower) ||
		(principal.email || '').toLowerCase().includes(searchLower),
	getPrincipalId: (principal) => principal.id || principal.email,
	getPrincipalPrimaryText: (principal) => principal.name || principal.email,
	getPrincipalSecondaryText: (principal) => principal.email,
	useAllQuery: useAllQuery.useUsersQuery,
	useSearchQuery: useSearchQuery.useSearchUsersQuery,
	mapItem: (item) => ({
		...item,
		id: item.email || item.Email,
		name: `${item.firstName || item.FirstName || ''} ${item.lastName || item.LastName || ''}`.trim() || item.email || item.Email,
		email: item.email || item.Email,
	}),
	scopes: [
		{
			key: 'projects',
			label: 'Projects',
			getItems: (principal) => principal.projects || [],
			addLabel: 'Add Project',
			onAdd: (principal) => handleAddProject(principal),
			onRemove: (principal, item) => handleRemoveProject(principal.id, item.projectId),
			getItemKey: (item) => item.projectId,
			getItemText: (item) => item.projectCode,
			getItemSecondaryText: () => null,
			fetchItems: (principal, skip, take) => fetchUserProjects(principal.email, skip, take),
		},
		{
			key: 'roles',
			label: 'Roles',
			getItems: (principal) => principal.roles || [],
			addLabel: 'Manage Roles',
			onAdd: (principal) => handleAddRole(principal),
			onRemove: (principal, item) => handleRemoveRole(principal.id, item.id || item.name),
			getItemKey: (item) => item.id || item.name,
			getItemText: (item) => item.name,
			getItemSecondaryText: () => null,
			fetchItems: (principal, skip, take) => fetchUserRoles(principal.email, skip, take),
		},
	],
});

export const createProjectsTabConfig = ({
	handleAddUserToProject,
	handleRemoveUserFromProject,
	fetchProjectUsers,
	useAllQuery,
	useSearchQuery,
}) => ({
	key: 'projects',
	label: 'Projects',
	search: {
		mode: 'server',
		placeholder: 'Project code or name',
		debounceMs: 400,
		minChars: 1,
	},
	searchDataModel: 'managedProjects',
	emptyStateLabel: 'projects',
	getData: () => [],
	filterPrincipal: (principal, searchLower) =>
		(principal.code || '').toLowerCase().includes(searchLower) ||
		(principal.description || '').toLowerCase().includes(searchLower),
	getPrincipalId: (principal) => principal.id || principal.code,
	getPrincipalPrimaryText: (principal) => principal.code || principal.name,
	getPrincipalSecondaryText: (principal) => principal.description,
	useAllQuery: useAllQuery.useManagedProjectsQuery,
	useSearchQuery: useSearchQuery.useSearchProjectsQuery,
	mapItem: (item) => ({
		...item,
		id: item.projectId,
		code: item.projectCode,
		name: item.projectCode,
		description: item.projectTitle,
	}),
	scopes: [
		{
			key: 'users',
			label: 'Users',
			getItems: (principal) => principal.users || [],
			addLabel: 'Add User',
			onAdd: (principal) => handleAddUserToProject(principal),
			onRemove: (principal, item) => handleRemoveUserFromProject(principal.id || principal.projectId, item),
			getItemKey: (item) => item.id || item.email,
			getItemText: (item) => item.name || item.email,
			getItemSecondaryText: (item) => item.email,
			fetchItems: (principal, skip, take) => fetchProjectUsers(principal.id || principal.projectId, skip, take),
		},
	],
});

export const createRolesTabConfig = ({
	handleAddUserToRole,
	handleRemoveUserFromRole,
	handleAddPermissionToRole,
	handleResetRolePermissions,
	handleRemovePermissionFromRole,
	isResetDisabled,
	canEditRolePermissions,
	fetchRoleUsers,
	fetchRolePermissions,
	useAllQuery,
	useSearchQuery,
}) => ({
	key: 'roles',
	label: 'Roles',
	search: {
		mode: 'server',
		placeholder: 'Role name',
		debounceMs: 400,
		minChars: 1,
	},
	emptyStateLabel: 'roles',
	getData: () => [],
	filterPrincipal: (principal, searchLower) =>
		(principal.name || '').toLowerCase().includes(searchLower) ||
		(principal.description || '').toLowerCase().includes(searchLower),
	getPrincipalId: (principal) => principal.name,
	getPrincipalPrimaryText: (principal) => principal.name,
	getPrincipalSecondaryText: (principal) => principal.description,
	useAllQuery: useAllQuery.useRolesQuery,
	useSearchQuery: useSearchQuery.useSearchRolesQuery,
	mapItem: (item) => ({
		...item,
		id: item.name,
		name: item.name,
		description: item.description,
	}),
	scopes: [
		{
			key: 'users',
			label: 'Users',
			getItems: (principal) => principal.users || [],
			addLabel: 'Add User',
			onAdd: (principal) => handleAddUserToRole(principal),
			onRemove: (principal, item) => handleRemoveUserFromRole(principal.id, item.id),
			getItemKey: (item) => item.id || item.email,
			getItemText: (item) => item.name || item.email,
			getItemSecondaryText: (item) => item.email,
			fetchItems: (principal, skip, take) => fetchRoleUsers(principal.name, skip, take),
		},
		{
			key: 'permissions',
			label: 'Permissions',
			canMutate: canEditRolePermissions,
			addLabel: 'Manage Permissions',
			onAdd: (principal) => handleAddPermissionToRole(principal),
			resetLabel: 'Reset Permissions',
			onReset: (principal) => handleResetRolePermissions(principal),
			isResetDisabled,
			onRemove: (principal, item) => handleRemovePermissionFromRole(principal.id, item.id || item.name),
			getItemKey: (item) => item.id || item.name,
			getItemText: (item) => item.name || item.id,
			getItemSecondaryText: () => null,
			fetchItems: (principal, skip, take) => fetchRolePermissions(principal.name, skip, take),
		},
	],
});

export const createAccessTabsConfig = ({ usersTabConfig, projectsTabConfig, rolesTabConfig }) => [
	usersTabConfig,
	projectsTabConfig,
	rolesTabConfig,
];
